/*
 * BN PDP V2 — hero and purchase (<bn-pdp-hero>).
 *
 * Responsibilities (add to cart itself is Dawn's <product-form>):
 *  - resolve the selected variant from native radio options
 *  - update the posted variant id, price, add-to-cart / sold-out state,
 *    payment button and back-in-stock form
 *  - mark option values that are sold out with the other current choices
 *  - keep ?variant= in the URL (replaceState) so links and reloads restore it
 *  - show the variant's featured image in the gallery
 *  - dispatch `bn:variant-change` (bubbles to document) so later sections can
 *    react without touching the purchase logic:
 *      detail = { sectionId, productId, variant, options: { [index]: value } }
 *
 * Variant data and strings are rendered by Liquid in JSON script tags, with
 * prices already formatted by Shopify's money filter for the active market.
 */
if (!customElements.get('bn-pdp-hero')) {
  class BnPdpHero extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.variants = this.readJson('[data-variants]') || [];
      this.strings = this.readJson('[data-strings]') || {};
      this.fieldsets = Array.from(this.querySelectorAll('.bn-hero__option'));
      this.idInput = this.querySelector('[data-variant-id]');
      this.addButton = this.querySelector('[data-add-button]');
      this.addLabel = this.querySelector('[data-add-label]');
      this.priceCurrent = this.querySelector('[data-bn-price]');
      this.priceCompare = this.querySelector('[data-bn-compare]');
      this.payment = this.querySelector('[data-payment]');
      this.bisForm = this.querySelector('[data-bis]');
      this.track = this.querySelector('[data-track]');
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));

      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-option-value]')) this.onOptionChange();
      });

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.goToSlide(Number(thumb.dataset.thumb)));
      });

      if (this.track) {
        this.track.addEventListener('scroll', () => this.onTrackScroll(), { passive: true });
        this.track.addEventListener('keydown', (event) => this.onTrackKeydown(event));
        // Clicking the main image advances to the next one (wraps around).
        this.track.addEventListener('click', (event) => {
          if (event.target.closest('video, button, a')) return;
          if (this.slides.length < 2) return;
          this.goToSlide((this.currentIndex() + 1) % this.slides.length);
        });
      }

      if (this.bisForm) this.bisForm.addEventListener('submit', (event) => this.onBackInStock(event));

      // Restore the UI when the page is shown from the back/forward cache.
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.update({ updateUrl: false, scrollGallery: false });
      });

      this.update({ updateUrl: false, scrollGallery: false, announce: false });

      const initialMedia = this.dataset.initialMedia;
      if (initialMedia) this.goToMedia(initialMedia, false);

      this.initStickyBar();
    }

    disconnectedCallback() {
      if (this.stickyCheck) {
        window.removeEventListener('scroll', this.stickyCheck);
        window.removeEventListener('resize', this.stickyCheck);
        if (this.panel) this.panel.removeEventListener('scroll', this.stickyCheck);
      }
      if (this.stickyUnsubscribers) this.stickyUnsubscribers.forEach((unsubscribe) => unsubscribe());
      document.documentElement.classList.remove('bn-sticky-buy-active');
    }

    // ── Sticky purchase bar ─────────────────────────────────────
    // No form of its own: it mirrors the selected variant and submits the
    // existing product form, so <product-form> handles add-to-cart, errors
    // and the cart drawer exactly as for the main button.
    initStickyBar() {
      this.sticky = this.querySelector('[data-sticky-buy]');
      if (!this.sticky || !this.addButton) return;
      this.form = this.querySelector('form[data-type="add-to-cart-form"]');
      this.stickyButton = this.sticky.querySelector('[data-sticky-add]');
      this.stickyLabel = this.sticky.querySelector('[data-sticky-label]');
      this.stickyPrice = this.sticky.querySelector('[data-sticky-price]');
      this.stickyVariant = this.sticky.querySelector('[data-sticky-variant]');

      // Visible whenever the main Add to cart is out of sight and already
      // passed: scrolled above the viewport, or scrolled away inside the
      // desktop panel. Hidden while it is visible or still further down.
      // Checked on scroll (rAF-throttled) rather than IntersectionObserver,
      // which misses jumps from "below" straight to "above" the viewport.
      this.panel = this.querySelector('[data-panel]');
      let queued = false;
      this.stickyCheck = () => {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(() => {
          queued = false;
          this.setStickyVisible(this.addButtonPassed());
        });
      };
      window.addEventListener('scroll', this.stickyCheck, { passive: true });
      window.addEventListener('resize', this.stickyCheck);
      if (this.panel) this.panel.addEventListener('scroll', this.stickyCheck, { passive: true });
      this.stickyCheck();

      this.addEventListener('bn:variant-change', (event) => this.syncSticky(event.detail.variant));

      this.stickyButton.addEventListener('click', () => {
        if (!this.form || this.addButton.disabled || this.addButton.getAttribute('aria-disabled') === 'true') return;
        this.stickyButton.classList.add('loading');
        this.stickyButton.setAttribute('aria-busy', 'true');
        if (typeof this.form.requestSubmit === 'function') {
          this.form.requestSubmit(this.addButton);
        } else {
          this.addButton.click();
        }
        window.clearTimeout(this.stickyTimer);
        this.stickyTimer = window.setTimeout(() => this.stopStickyLoading(), 8000);
      });

      this.stickyUnsubscribers = [];
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.stickyUnsubscribers.push(subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.stopStickyLoading()));
        this.stickyUnsubscribers.push(subscribe(PUB_SUB_EVENTS.cartError, () => this.stopStickyLoading()));
      }
    }

    addButtonPassed() {
      const button = this.addButton.getBoundingClientRect();
      if (button.top >= window.innerHeight) return false; // still ahead
      let top = 0;
      let bottom = window.innerHeight;
      if (this.panel && this.panel.scrollHeight > this.panel.clientHeight) {
        const panel = this.panel.getBoundingClientRect();
        top = Math.max(top, panel.top);
        bottom = Math.min(bottom, panel.bottom);
      }
      const visible = button.bottom > top && button.top < bottom;
      return !visible;
    }

    setStickyVisible(visible) {
      if (this.sticky && this.sticky.hidden === !visible) return;
      if (!this.sticky) return;
      this.sticky.hidden = !visible;
      this.sticky.classList.toggle('is-visible', visible);
      document.documentElement.classList.toggle('bn-sticky-buy-active', visible);
    }

    stopStickyLoading() {
      if (!this.stickyButton) return;
      window.clearTimeout(this.stickyTimer);
      this.stickyButton.classList.remove('loading');
      this.stickyButton.removeAttribute('aria-busy');
    }

    syncSticky(variant) {
      if (!this.sticky) return;
      const available = Boolean(variant && variant.available);
      if (this.stickyPrice && variant) this.stickyPrice.textContent = variant.price;
      if (this.stickyVariant && variant) this.stickyVariant.textContent = variant.options.join(' · ');
      if (this.stickyButton) {
        this.stickyButton.disabled = !available;
        if (available) {
          this.stickyButton.removeAttribute('aria-disabled');
        } else {
          this.stickyButton.setAttribute('aria-disabled', 'true');
        }
      }
      if (this.stickyLabel) {
        this.stickyLabel.textContent = !variant
          ? this.strings.unavailable
          : available
          ? this.strings.addToCart
          : this.strings.soldOut;
      }
    }

    readJson(selector) {
      const el = this.querySelector(selector);
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (error) {
        console.error('bn-pdp-hero: invalid JSON in', selector, error);
        return null;
      }
    }

    selectedOptions() {
      return this.fieldsets.map((fieldset) => {
        const checked = fieldset.querySelector('[data-option-value]:checked');
        return checked ? checked.value : null;
      });
    }

    findVariant(options) {
      // Products without real options (only "Default Title") render no option
      // groups: their single variant is always the selected one.
      if (!this.fieldsets.length) return this.variants[0] || null;
      return this.variants.find((variant) => variant.options.every((value, i) => value === options[i])) || null;
    }

    onOptionChange() {
      this.update({ updateUrl: true, scrollGallery: true, announce: true });
    }

    update({ updateUrl, scrollGallery, announce = true }) {
      const options = this.selectedOptions();
      const variant = this.variants.length ? this.findVariant(options) : null;

      this.fieldsets.forEach((fieldset, i) => {
        const valueEl = fieldset.querySelector('[data-selected-value]');
        if (valueEl) valueEl.textContent = options[i] || '';
      });

      this.markAvailability(options);
      this.renderPurchaseState(variant);
      if (this.sticky) this.syncSticky(variant);

      if (!variant) return;

      if (updateUrl) this.updateUrl(variant.id);
      if (scrollGallery && variant.media) this.goToMedia(variant.media, true);

      if (announce) {
        this.dispatchEvent(
          new CustomEvent('bn:variant-change', {
            bubbles: true,
            detail: {
              sectionId: this.dataset.sectionId,
              productId: Number(this.dataset.productId),
              variant,
              options: Object.assign({}, options),
            },
          })
        );
      }
    }

    // A value is "sold out" when no available variant has it together with the
    // customer's current choices for the other options.
    markAvailability(options) {
      this.fieldsets.forEach((fieldset, index) => {
        fieldset.querySelectorAll('[data-option-value]').forEach((input) => {
          const candidate = options.slice();
          candidate[index] = input.value;
          const match = this.findVariant(candidate);
          const unavailable = !match || !match.available;
          const label = this.querySelector(`label[for="${input.id}"]`);
          if (!label) return;
          label.classList.toggle('is-unavailable', unavailable);
          const note = label.querySelector('[data-unavailable-text]');
          if (note) note.hidden = !unavailable;
        });
      });
    }

    renderPurchaseState(variant) {
      const available = Boolean(variant && variant.available);

      if (this.idInput && variant) this.idInput.value = variant.id;

      if (this.priceCurrent && variant) this.priceCurrent.textContent = variant.price;
      if (this.priceCompare) {
        const showCompare = Boolean(variant && variant.compare);
        this.priceCompare.hidden = !showCompare;
        this.priceCompare.textContent = showCompare ? variant.compare : '';
      }

      if (this.addButton) {
        this.addButton.disabled = !available;
        if (available) {
          this.addButton.removeAttribute('aria-disabled');
        } else {
          this.addButton.setAttribute('aria-disabled', 'true');
        }
        // Reset a sold-out message left by <product-form> after a failed add.
        const soldOut = this.addButton.querySelector('.sold-out-message');
        if (soldOut) soldOut.classList.add('hidden');
        if (this.addLabel) {
          this.addLabel.classList.remove('hidden');
          this.addLabel.textContent = !variant
            ? this.strings.unavailable
            : available
            ? this.strings.addToCart
            : this.strings.soldOut;
        }
      }

      const productForm = this.querySelector('product-form');
      if (productForm) {
        productForm.error = false;
        if (typeof productForm.handleErrorMessage === 'function') productForm.handleErrorMessage();
      }

      if (this.payment) this.payment.hidden = !available;
      if (this.bisForm) {
        this.bisForm.hidden = !variant || available;
        const status = this.bisForm.querySelector('[data-bis-status]');
        if (status) status.textContent = '';
      }
    }

    updateUrl(variantId) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState(window.history.state, '', url.toString());
    }

    // ── Gallery ───────────────────────────────────────────────────
    goToMedia(mediaId, smooth) {
      const slide = this.slides.find((s) => s.dataset.mediaId === String(mediaId));
      if (slide) this.goToSlide(Number(slide.dataset.index), smooth);
    }

    goToSlide(index, smooth = true) {
      const slide = this.slides[index];
      if (!slide || !this.track) return;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({
        left: slide.offsetLeft, // the track is position: relative, so this is track-relative
        behavior: smooth && !reduceMotion ? 'smooth' : 'auto',
      });
      this.setActiveThumb(index);
    }

    currentIndex() {
      return Math.round(this.track.scrollLeft / (this.track.clientWidth || 1));
    }

    onTrackScroll() {
      window.clearTimeout(this.scrollTimer);
      this.scrollTimer = window.setTimeout(() => {
        const width = this.track.clientWidth || 1;
        this.setActiveThumb(Math.round(this.track.scrollLeft / width));
      }, 80);
    }

    onTrackKeydown(event) {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      event.preventDefault();
      const current = this.currentIndex();
      const next = Math.min(this.slides.length - 1, Math.max(0, current + (event.key === 'ArrowRight' ? 1 : -1)));
      this.goToSlide(next);
    }

    setActiveThumb(index) {
      this.thumbs.forEach((thumb) => {
        const active = Number(thumb.dataset.thumb) === index;
        thumb.classList.toggle('is-active', active);
        if (active) {
          thumb.setAttribute('aria-current', 'true');
          // Keep the active thumbnail visible inside the (scrollable) thumbnail strip.
          const strip = thumb.parentElement;
          if (strip && strip.scrollHeight > strip.clientHeight) {
            const top = thumb.offsetTop - strip.offsetTop;
            if (top < strip.scrollTop || top + thumb.offsetHeight > strip.scrollTop + strip.clientHeight) {
              strip.scrollTo({ top: top - (strip.clientHeight - thumb.offsetHeight) / 2, behavior: 'auto' });
            }
          }
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
    }

    // ── Back in stock (Klaviyo client API, public site key) ───────
    async onBackInStock(event) {
      event.preventDefault();
      const form = this.bisForm;
      const input = form.querySelector('input[type="email"]');
      const status = form.querySelector('[data-bis-status]');
      const button = form.querySelector('button[type="submit"]');
      const email = input ? input.value.trim() : '';

      if (!input || !input.checkValidity() || !email) {
        status.textContent = this.strings.bisInvalid;
        if (input) input.focus();
        return;
      }

      const variantId = this.idInput ? this.idInput.value : '';
      button.disabled = true;
      status.textContent = '';

      try {
        const response = await fetch(
          `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(form.dataset.klaviyoKey)}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', revision: '2024-06-15' },
            body: JSON.stringify({
              data: {
                type: 'back-in-stock-subscription',
                attributes: {
                  channels: ['EMAIL'],
                  profile: { data: { type: 'profile', attributes: { email } } },
                },
                relationships: {
                  variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } },
                },
              },
            }),
          }
        );
        if (!response.ok) throw new Error(`Klaviyo responded ${response.status}`);
        status.textContent = this.strings.bisSuccess;
        input.value = '';
      } catch (error) {
        console.error('bn-pdp-hero: back-in-stock signup failed', error);
        status.textContent = this.strings.bisError;
      } finally {
        button.disabled = false;
      }
    }
  }

  customElements.define('bn-pdp-hero', BnPdpHero);
}

/*
 * <bn-free-shipping> — progress towards a merchant-set free-shipping
 * threshold (cents, in the cart's currency; rendered by Liquid for the
 * active market). Re-reads /cart.js whenever the theme publishes a cart
 * update (Dawn pubsub: product forms, add-ons, cart drawer quantity changes).
 */
if (!customElements.get('bn-free-shipping')) {
  class BnFreeShipping extends HTMLElement {
    connectedCallback() {
      this.threshold = Number(this.dataset.threshold) || 0;
      this.text = this.querySelector('[data-shipping-text]');
      this.fill = this.querySelector('[data-shipping-fill]');
      this.bar = this.querySelector('[role="progressbar"]');
      if (!this.threshold) return;
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refresh());
      }
      this.refresh();
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    async refresh() {
      try {
        const cart = await (await fetch(`${window.routes?.cart_url || '/cart'}.js`)).json();
        this.render(cart);
      } catch (error) {
        console.error('bn-free-shipping: could not read cart', error);
      }
    }

    // Format like Shopify's own `money` output for this market: Liquid renders
    // 123456 cents as a sample (e.g. "1.234,56 kr"), and the same separators
    // and symbol placement are reused for the live amount.
    format(cents) {
      const sample = this.dataset.moneySample || '';
      const match = sample.match(/1(\D?)234(\D)56/);
      const amount = (cents / 100).toFixed(2);
      if (!match) return amount;
      const [whole, decimals] = amount.split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, match[1]);
      return sample.replace(match[0], `${grouped}${match[2]}${decimals}`);
    }

    render(cart) {
      const total = cart.total_price;
      const remaining = Math.max(0, this.threshold - total);
      const percent = Math.min(100, Math.round((total / this.threshold) * 100));
      if (this.fill) this.fill.style.width = `${percent}%`;
      if (this.bar) this.bar.setAttribute('aria-valuenow', String(percent));
      if (this.text) {
        this.text.textContent = remaining > 0
          ? this.dataset.textRemaining.replace('[amount]', this.format(remaining))
          : this.dataset.textReached;
      }
    }
  }

  customElements.define('bn-free-shipping', BnFreeShipping);
}
